import { useCallback, useEffect, useMemo } from 'react';

import { useBxpWebStargateClient } from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client';
import { FastshiftRollUpStatus } from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';

import { getOrgQueryParam } from '@/common/utils';
import {
	useFastshiftDispatch,
	useFastshiftSelector,
	getFastshiftTopSupportRequests,
	FastshiftDispatchPromise,
} from '@/state';

type UseTopMigrationTickets = () => {
	pending: boolean;
	error: string | null;
	supportTickets: {
		supportTicketId: string;
		title: string;
		status: FastshiftRollUpStatus;
	}[];
	total: number;
	handleRefresh: (customerDomainId: string) => FastshiftDispatchPromise<unknown>;
};

const TOP_SUPPORT_REQUESTS_COUNT = 3;

export const useTopMigrationTickets: UseTopMigrationTickets = () => {
	const dispatch = useFastshiftDispatch();

	const selectedDomainOrg = useFastshiftSelector((state) => state.selectedDomainOrg);

	const { data: customerDomainData } = useFastshiftSelector((state) => state.customerDomain);

	const stargateClient = useBxpWebStargateClient();

	const {
		pending,
		error,
		data: topSupportRequestsData,
		lastFetchParams,
	} = useFastshiftSelector((state) => state.topSupportRequests);

	const supportTicketQueryParams = useMemo(() => {
		return selectedDomainOrg ? getOrgQueryParam(selectedDomainOrg) : {};
	}, [selectedDomainOrg]);

	const getTopSupportRequests = useCallback(
		(customerDomainId: string) => {
			return dispatch(
				getFastshiftTopSupportRequests({
					stargateClient,
					domainId: customerDomainId,
					count: TOP_SUPPORT_REQUESTS_COUNT,
					queryParams: supportTicketQueryParams,
				}),
			);
		},
		[dispatch, stargateClient, supportTicketQueryParams],
	);

	useEffect(() => {
		let topSupportRequestsPromise: FastshiftDispatchPromise<unknown> | null = null;

		if (customerDomainData && selectedDomainOrg) {
			// Check if we need to fetch based on lastFetchParams
			const needsFetch =
				!lastFetchParams ||
				lastFetchParams.customerDomainId !== customerDomainData.customerDomainId ||
				lastFetchParams.queryParams.orgId !== supportTicketQueryParams.orgId ||
				lastFetchParams.queryParams.permissionLevel !== supportTicketQueryParams.permissionLevel;

			// Skip fetch if data already cached for these params
			if (!needsFetch) {
				return;
			}

			topSupportRequestsPromise = getTopSupportRequests(customerDomainData.customerDomainId);
		}

		return () => {
			if (topSupportRequestsPromise) {
				topSupportRequestsPromise.abort();
			}
		};
	}, [
		dispatch,
		selectedDomainOrg,
		stargateClient,
		customerDomainData,
		supportTicketQueryParams,
		lastFetchParams,
		getTopSupportRequests,
	]);

	return {
		pending,
		error,
		...(topSupportRequestsData ? topSupportRequestsData : { supportTickets: [], total: 0 }),
		handleRefresh: getTopSupportRequests,
	};
};
